import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useParams, Link } from 'react-router-dom';
import { useSeries } from '../../App';
import {
  ChevronLeft,
  Search,
  Save,
  RefreshCw,
  Download,
  Printer,
  Copy,
  Users,
  Shuffle,
  AlertCircle,
  CheckCircle2,
  X,
  Trash2,
  Edit,
  GripVertical,
  UserPlus,
  User,
  Phone,
  Mail,
  MapPin,
  Car,
  Flag,
  Loader2,
  DollarSign,
  Wrench,
  Calendar
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useApi } from '../../contexts/ApiContext';
import CopyEntriesModal from '../../components/events/CopyEntriesModal';
import AssignToRaceModal from '../../components/events/AssignToRaceModal';

// Driver Confirmation Modal - used for both adding and editing entries
function DriverConfirmationModal({
  isOpen,
  onClose,
  driver,
  entry,
  mode = 'add',
  onConfirm,
  loading: confirmLoading
}) {
  const api = useApi();
  const [carNumber, setCarNumber] = useState(() => {
    if (mode === 'edit' && entry) return entry.car_number || '';
    if (driver) return driver.car_number || '';
    return '';
  });
  const [pill, setPill] = useState(() => (mode === 'edit' && entry) ? (entry.pill || '') : '');
  const [payeeName, setPayeeName] = useState(() => (mode === 'edit' && entry) ? (entry.payee_name || '') : '');
  const [notes, setNotes] = useState(() => (mode === 'edit' && entry) ? (entry.notes || '') : '');
  const [dateOfBirth, setDateOfBirth] = useState(() => {
    if (mode === 'edit' && entry?.date_of_birth) {
      return new Date(entry.date_of_birth).toISOString().split('T')[0];
    }
    return '';
  });

  const driverId = mode === 'edit' ? (entry?.driver_ID || entry?.driver_id) : driver?.driver_ID;

  const { data: driverDetails, isLoading: loading } = useQuery({
    queryKey: ['driver-details', driverId],
    queryFn: async () => {
      const result = await api.drivers.getById(driverId);
      if (!result.success) throw new Error(result.message || 'Failed to fetch driver');
      const driverData = result.data?.driver || result.data;
      if (driverData) {
        setPayeeName(`${driverData.first_name} ${driverData.last_name}`);
      }
      return driverData;
    },
    enabled: isOpen && !!driverId,
  });

  const handleConfirm = () => {
    if (mode === 'edit') {
      onConfirm({
        ...entry,
        car_number: carNumber,
        pill: pill !== '' && pill != null ? parseInt(pill, 10) : null,
        payee_name: payeeName,
        notes: notes,
        date_of_birth: dateOfBirth || null
      });
    } else {
      onConfirm({
        ...driver,
        car_number: carNumber,
        payee_name: payeeName,
        notes: notes
      });
    }
  };

  const isEditMode = mode === 'edit';
  const displayName = isEditMode ? entry?.driver_name : (driver?.name || `${driverDetails?.first_name} ${driverDetails?.last_name}`);

  if (!isOpen) return null;

  const d = driverDetails;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
              {isEditMode ? <Edit className="w-4 h-4 text-white" /> : <UserPlus className="w-4 h-4 text-white" />}
            </div>
            <div>
              <h2 className="text-md font-semibold text-white">{isEditMode ? 'Edit Entry' : 'Confirm Entry'}</h2>
              <p className="text-xs text-slate-300">
                {isEditMode ? displayName : 'Review driver information before adding'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : d ? (
            <div className="space-y-6">
              {/* Driver Header */}
              <div className="flex items-start gap-4">
                {/* Headshot */}
                <div className="flex-shrink-0">
                  {d.headshot_url ? (
                    <img
                      src={d.headshot_url}
                      alt={`${d.first_name} ${d.last_name}`}
                      className="w-24 h-24 rounded-lg object-cover border-2 border-slate-200"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center border-2 border-slate-200">
                      <User className="w-10 h-10 text-slate-400" />
                    </div>
                  )}
                </div>

                {/* Name & Basic Info */}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800">
                    {d.first_name} {d.last_name}
                    {d.name_suffix && <span className="text-slate-500"> {d.name_suffix}</span>}
                  </h3>
                  {d.nickname && (
                    <p className="text-slate-500">"{d.nickname}"</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {d.series_names && (
                      <Badge variant="primary">{d.series_names}</Badge>
                    )}
                    {d.status && (
                      <Badge variant={d.status === 'Active' ? 'success' : 'neutral'}>{d.status}</Badge>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex-shrink-0 text-right">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-slate-800">{d.starts || 0}</div>
                      <div className="text-xs text-slate-500 uppercase">Starts</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-600">{d.wins || 0}</div>
                      <div className="text-xs text-slate-500 uppercase">Wins</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact & Location Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Contact Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Information
                  </h4>
                  {d.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">{d.email}</span>
                    </div>
                  )}
                  {d.mobile_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">{d.mobile_phone}</span>
                    </div>
                  )}
                  {!d.email && !d.mobile_phone && (
                    <p className="text-sm text-slate-400 italic">No contact info on file</p>
                  )}
                </div>

                {/* Location Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </h4>
                  {(d.hometown || d.city) ? (
                    <div className="text-sm text-slate-600">
                      <p>{d.city || d.hometown}{d.state_code && `, ${d.state_code}`}</p>
                      {d.street_address_1 && <p className="text-slate-400">{d.street_address_1}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No location on file</p>
                  )}
                </div>
              </div>

              {/* Team & Equipment */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <Flag className="w-4 h-4" />
                    Team
                  </h4>
                  <p className="text-sm text-slate-600">{d.team_name || 'Independent / Owner-Operator'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <Wrench className="w-4 h-4" />
                    Chassis
                  </h4>
                  <p className="text-sm text-slate-600">{d.chassis || 'Not specified'}</p>
                </div>
              </div>

              {/* Divider */}
              <hr className="border-slate-200" />

              {/* Entry Details Form */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Entry Details
                </h4>

                <div className={`grid ${isEditMode ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
                  {/* Car Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Car Number
                    </label>
                    <Input
                      value={carNumber}
                      onChange={(e) => setCarNumber(e.target.value)}
                      placeholder="Enter car number"
                      className="w-full"
                    />
                  </div>

                  {/* Pill Number - only show in edit mode */}
                  {isEditMode && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Pill #
                      </label>
                      <Input
                        type="number"
                        value={pill}
                        onChange={(e) => setPill(e.target.value)}
                        placeholder="Pill"
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Payee Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <DollarSign className="w-3 h-3 inline mr-1" />
                      Payee Name (for winnings)
                    </label>
                    <Input
                      value={payeeName}
                      onChange={(e) => setPayeeName(e.target.value)}
                      placeholder="Who gets paid?"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Birthdate - only show in edit mode */}
                {isEditMode && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Date of Birth
                    </label>
                    <Input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      className="w-48"
                    />
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes about this entry..."
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Could not load driver details</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={confirmLoading || loading}
            leftIcon={confirmLoading ? Loader2 : (isEditMode ? Save : CheckCircle2)}
            className={confirmLoading ? 'animate-pulse' : ''}
          >
            {confirmLoading
              ? (isEditMode ? 'Saving...' : 'Adding...')
              : (isEditMode ? 'Save Changes' : 'Confirm & Add Entry')
            }
          </Button>
        </div>
      </div>
    </div>
  );
}

// Inline pill input - saves on blur or Enter
function InlinePillInput({ value, onChange }) {
  const [localValue, setLocalValue] = useState(value ?? '');

  const handleBlur = () => {
    const parsed = localValue === '' ? null : parseInt(localValue, 10);
    if (parsed !== (value || null)) {
      onChange(parsed);
    }
  };

  return (
    <input
      type="number"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
      }}
      onClick={(e) => e.stopPropagation()}
      min="1"
      className="w-14 h-7 text-center font-mono text-sm font-bold rounded border border-slate-200
        bg-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400 focus:outline-none
        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      placeholder="-"
    />
  );
}

// Draggable Entry Card in the main list
function DraggableEntryCard({
  entry,
  index,
  onEdit,
  onDelete,
  onPillChange,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
  dragOverIndex
}) {
  const isDropTarget = dragOverIndex === index;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index, 'entry')}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={`
        group bg-white border rounded-lg p-3 mb-2 transition-all duration-200 shadow-sm cursor-grab active:cursor-grabbing
        ${isDragging ? 'opacity-50 scale-[0.98]' : 'opacity-100'}
        ${isDropTarget ? 'border-wrg-blue-600 border-2 bg-wrg-blue-600-50' : 'border-slate-200 hover:border-slate-300'}
      `}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <div className="flex-shrink-0 text-slate-300 group-hover:text-slate-500 transition-colors">
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Position Number */}
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-wrg-blue-600 flex items-center justify-center">
          <span className="text-xs font-bold text-white">{index + 1}</span>
        </div>

        {/* Car Number */}
        <div className="flex-shrink-0 w-14">
          <span className="font-mono font-bold text-lg text-slate-800">
            {entry.car_number || '-'}
          </span>
        </div>

        {/* Driver Info */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-slate-700 truncate">{entry.driver_name}</div>
          <div className="text-xs text-slate-500 truncate">
            {entry.team || entry.location || ''}
          </div>
        </div>

        {/* Pill */}
        <div className="flex-shrink-0 w-16 text-center">
          <InlinePillInput
            key={entry.id}
            value={entry.pill}
            onChange={(newPill) => onPillChange(entry.id, newPill)}
          />
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
              className="p-1"
            >
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
              className="p-1 text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Driver card in the sidebar (available drivers)
function DriverSidebarCard({ driver, onDragStart, onClick }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, driver, 'driver')}
      onClick={() => onClick(driver)}
      className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg
        cursor-pointer hover:border-slate-300 hover:bg-slate-100 transition-all group"
    >
      <GripVertical className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 cursor-grab active:cursor-grabbing" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-700 truncate">{driver.name}</span>
          {driver.location && (
            <span className="text-xs text-slate-500 truncate flex-shrink-0">{driver.location}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {driver.car_number && `#${driver.car_number}`}
          {driver.car_number && driver.team && ' • '}
          {driver.team}
        </div>
      </div>
      <UserPlus className="w-4 h-4 text-slate-400 group-hover:text-wrg-blue-600 flex-shrink-0" />
    </div>
  );
}

export default function EventEntries() {
  const { seriesSlug, eventId } = useParams();
  const currentSeries = useSeries();
  const api = useApi();

  const [entries, setEntries] = useState([]);
  const [selectedRaceEventId, setSelectedRaceEventId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('last_name');
  const [driverSearchTerm, setDriverSearchTerm] = useState('');

  // Drag and drop state
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedDriver, setDraggedDriver] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);

  // Entry modal state (used for both add and edit)
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryModalMode, setEntryModalMode] = useState('add');
  const [pendingDriver, setPendingDriver] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);

  // Copy entries modal state
  const [copyModalOpen, setCopyModalOpen] = useState(false);

  // Assign to race modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const { data: event, isLoading: eventLoading, error: eventError } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const result = await api.events.getById(eventId);
      if (!result.success) throw new Error(result.message || 'Failed to fetch event');
      return result.data;
    },
    enabled: !!eventId,
  });

  const { data: raceEvents = [], isLoading: raceEventsLoading } = useQuery({
    queryKey: ['event-race-events', eventId],
    queryFn: async () => {
      const raceEventsData = await api.apiCall(`/api/events/${eventId}/race-events`);
      const events = raceEventsData || [];
      if (events.length > 0 && !selectedRaceEventId) {
        const matchingSeries = currentSeries?.dbId
          ? events.find(re => re.wrg_series_ID === currentSeries.dbId)
          : null;
        setSelectedRaceEventId(matchingSeries ? matchingSeries.id : events[0].id);
      }
      return events;
    },
    enabled: !!eventId,
  });

  const { data: drivers = [], isLoading: driversLoading } = useQuery({
    queryKey: ['all-drivers'],
    queryFn: async () => {
      const result = await api.drivers.getAll();
      if (!result.success || !result.data) return [];
      const driversList = result.data.drivers || result.data;
      if (!Array.isArray(driversList)) return [];
      return driversList.map(d => ({
        driver_ID: d.driver_ID || d.id,
        name: d.driver_name || d.name || `${d.last_name}, ${d.first_name}`,
        car_number: d.car_number,
        team: d.team_name,
        series: d.series_names,
        location: d.hometown ? `${d.hometown}, ${d.home_state || ''}` : ''
      }));
    },
  });

  const { isLoading: entriesLoading, refetch: refetchEntries } = useQuery({
    queryKey: ['event-entries', eventId, selectedRaceEventId, sortOrder],
    queryFn: async () => {
      let url = `/api/events/${eventId}/entries?order=${sortOrder}`;
      if (selectedRaceEventId) {
        url += `&race_event_id=${selectedRaceEventId}`;
      }
      const entriesResponse = await api.apiCall(url);
      setEntries(entriesResponse || []);
      return entriesResponse || [];
    },
    enabled: !!eventId && !!selectedRaceEventId,
  });

  const loading = eventLoading || raceEventsLoading || driversLoading || entriesLoading;
  const [mutationError, setMutationError] = useState(null);
  const error = mutationError || eventError?.message || null;
  const setError = setMutationError;

  const fetchData = () => refetchEntries();

  // Filter entries by search
  const filteredEntries = entries.filter(entry =>
    entry.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.car_number?.toString().includes(searchTerm) ||
    entry.team?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const enteredDriverIds = new Set(entries.map(e => e.driver_ID || e.driver_id));
  const availableDrivers = drivers
    .filter(d => !enteredDriverIds.has(d.driver_ID))
    .filter(d =>
      d.name?.toLowerCase().includes(driverSearchTerm.toLowerCase()) ||
      d.car_number?.toString().includes(driverSearchTerm)
    );

  const driverListRef = useRef(null);
  const driverVirtualizer = useVirtualizer({
    count: availableDrivers.length,
    getScrollElement: () => driverListRef.current,
    estimateSize: () => 62,
    overscan: 8,
  });

  // Handle entry delete
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Remove this entry?')) return;

    try {
      await api.apiCall(`/api/events/${eventId}/entries/${entryId}`, {
        method: 'DELETE'
      });
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError('Failed to delete entry. Please try again.');
    }
  };

  // Open modal for adding a driver
  const handleAddDriver = (driver) => {
    setPendingDriver(driver);
    setEditingEntry(null);
    setEntryModalMode('add');
    setEntryModalOpen(true);
  };

  // Open modal for editing an entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setPendingDriver(null);
    setEntryModalMode('edit');
    setEntryModalOpen(true);
  };

  // Close the entry modal
  const handleCloseEntryModal = () => {
    setEntryModalOpen(false);
    setPendingDriver(null);
    setEditingEntry(null);
  };

  // Handle confirm from modal (add or edit)
  const handleModalConfirm = async (data) => {
    setSavingEntry(true);
    try {
      if (entryModalMode === 'edit') {
        // Update existing entry
        await api.apiCall(`/api/events/${eventId}/entries/${data.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          data: data
        });
        setEntries(prev => prev.map(e => e.id === data.id ? data : e));
      } else {
        // Add new entry
        const response = await api.apiCall(`/api/events/${eventId}/entries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: {
            driver_id: data.driver_ID,
            race_event_id: selectedRaceEventId,
            car_number: data.car_number || '',
            payee_name: data.payee_name || '',
            notes: data.notes || ''
          }
        });

        if (response.entry) {
          setEntries(prev => [...prev, response.entry]);
        } else {
          setEntries(prev => [...prev, {
            id: Date.now(),
            driver_ID: data.driver_ID,
            driver_name: data.name,
            car_number: data.car_number,
            team: data.team,
            location: data.location,
            payee_name: data.payee_name,
            notes: data.notes
          }]);
        }
      }

      handleCloseEntryModal();
    } catch (err) {
      console.error('Error saving entry:', err);
      setError('Failed to save entry. Please try again.');
    } finally {
      setSavingEntry(false);
    }
  };

  // Inline pill change (single entry)
  const handlePillChange = async (entryId, newPill) => {
    // Update locally
    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, pill: newPill } : e));

    // Save to backend
    try {
      await api.apiCall(`/api/events/${eventId}/entries/${entryId}`, {
        method: 'PUT',
        data: { pill: newPill }
      });
    } catch (err) {
      console.error('Error saving pill:', err);
      setError('Failed to save pill number.');
      fetchData(); // revert on error
    }
  };

  // Pill draw
  const handlePillDraw = async () => {
    if (!window.confirm('Assign random pill numbers to all entries?')) return;

    const shuffled = [...entries].sort(() => Math.random() - 0.5);
    const withPills = shuffled.map((entry, idx) => ({
      ...entry,
      pill: idx + 1
    }));
    setEntries(withPills);

    // Save pill draw to database
    setSaving(true);
    try {
      await api.apiCall(`/api/events/${eventId}/entries/save-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { entries: withPills }
      });
    } catch (err) {
      console.error('Error saving pill draw:', err);
      setError('Failed to save pill draw. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle imported entries from copy modal
  const handleImportComplete = (importedEntries) => {
    if (Array.isArray(importedEntries) && importedEntries.length > 0) {
      setEntries(prev => [...prev, ...importedEntries]);
      setError(null);
    }
  };

  // Drag handlers for reordering entries
  const handleDragStart = (e, index, type) => {
    if (type === 'entry') {
      setDragIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'entry', index }));
    } else if (type === 'driver') {
      setDraggedDriver(index); // index is actually the driver object here
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'driver', driver: index }));
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = draggedDriver ? 'copy' : 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = (e) => {
    e.preventDefault();

    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newEntries = [...filteredEntries];
      const [draggedItem] = newEntries.splice(dragIndex, 1);
      newEntries.splice(dragOverIndex, 0, draggedItem);
      setEntries(newEntries);
      setHasOrderChanged(true);
    }

    setDragIndex(null);
    setDragOverIndex(null);
    setDraggedDriver(null);
    setIsDraggingOver(false);
  };

  // Handle drop on the entries area (for adding new drivers)
  const handleDropOnEntriesArea = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type === 'driver' && data.driver) {
        handleAddDriver(data.driver);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }

    setDraggedDriver(null);
  };

  const handleDragOverEntriesArea = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingOver(true);
  };

  const handleDragLeaveEntriesArea = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  // Export entries to CSV
  const handleExportCSV = () => {
    if (entries.length === 0) {
      setError('No entries to export');
      return;
    }

    // Define CSV headers
    const headers = ['Position', 'Car Number', 'Driver Name', 'Team', 'Location', 'Pill'];

    // Build CSV rows
    const rows = entries.map((entry, index) => [
      index + 1,
      entry.car_number || '',
      entry.driver_name || '',
      entry.team || '',
      entry.location || '',
      entry.pill || ''
    ]);

    // Escape CSV values (handle commas, quotes, etc.)
    const escapeCSV = (value) => {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with event name and date
    const eventName = event?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'event';
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `${eventName}_entries_${dateStr}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Save order to server
  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const orderData = entries.map((entry, idx) => ({
        id: entry.id,
        position: idx + 1,
        pill: entry.pill
      }));

      await api.apiCall(`/api/events/${eventId}/entries/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: { entries: orderData }
      });

      setHasOrderChanged(false);
    } catch (err) {
      console.error('Error saving order:', err);
      setError('Failed to save order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-slate-200 rounded w-1/3" />
        <div className="flex gap-4">
          <div className="w-64 h-80 bg-slate-200 rounded" />
          <div className="flex-1 h-80 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Main Layout - Sidebar + Entries */}
      <div className="flex gap-4">
        {/* Left Sidebar - Available Drivers */}
        <div className="w-[400px] flex-shrink-0">
          <Card className="sticky top-4 overflow-hidden" padding="none">
            <div className="bg-wrg-blue-600 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Available Drivers</h3>
                <Badge variant="neutral" size="sm" className="bg-white/20 text-white">{availableDrivers.length}</Badge>
              </div>
            </div>
            <CardContent className="p-3">

              <Input
                placeholder="Search drivers..."
                value={driverSearchTerm}
                onChange={(e) => setDriverSearchTerm(e.target.value)}
                leftIcon={Search}
                size="sm"
                className="mb-3"
              />

              <div className="text-2xs text-slate-400 mb-2 flex items-center gap-1">
                <GripVertical className="w-3 h-3" />
                Drag to add to entries
              </div>

              <div
                ref={driverListRef}
                className="max-h-[calc(100vh-280px)] overflow-y-auto pr-1"
              >
                {availableDrivers.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">
                      {driverSearchTerm ? 'No drivers match search' : 'All drivers added'}
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      height: driverVirtualizer.getTotalSize(),
                      position: 'relative',
                    }}
                  >
                    {driverVirtualizer.getVirtualItems().map(virtualRow => {
                      const driver = availableDrivers[virtualRow.index];
                      return (
                        <div
                          key={driver.driver_ID}
                          ref={driverVirtualizer.measureElement}
                          data-index={virtualRow.index}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            transform: `translateY(${virtualRow.start}px)`,
                            paddingBottom: 6,
                          }}
                        >
                          <DriverSidebarCard
                            driver={driver}
                            onDragStart={handleDragStart}
                            onClick={handleAddDriver}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Entries List */}
        <div className="flex-1">
          {/* Breadcrumb */}
          <Link
            to={`/${seriesSlug}/events/${eventId}`}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-wrg-blue-600
              bg-slate-100 hover:bg-wrg-blue-50 px-2.5 py-1 rounded-full transition-colors mb-3"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span className="font-medium">Back to Event</span>
          </Link>

          {/* Header */}
          <div className="bg-gradient-to-r from-wrg-blue-900 to-wrg-blue-700 rounded-lg p-4 mb-3">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-lg font-semibold text-white">Event Entries</h1>
                <p className="text-xs text-wrg-blue-200 mt-0.5">
                  {event?.name} - {entries.length} entries
                  {raceEvents.length > 0 && selectedRaceEventId && (
                    <span className="ml-1 text-wrg-blue-300">
                      ({raceEvents.find(re => re.id === selectedRaceEventId)?.series_name || 'All'})
                    </span>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-1.5">
                {hasOrderChanged && (
                  <Button size="sm" onClick={handleSaveOrder} disabled={saving} leftIcon={Save}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
                <Button variant="ghost" size="xs" onClick={fetchData} disabled={loading} className="text-white hover:bg-white/10">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="secondary" size="xs" leftIcon={Printer} className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={() => window.open(api.events.getQualifyingPrintURL(eventId, selectedRaceEventId), '_blank')}>
                  Print
                </Button>
                <Button variant="secondary" size="xs" leftIcon={Download} className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={handleExportCSV}>
                  Export
                </Button>
              </div>
            </div>
          </div>
          {/* Series / Race Event Tabs */}
          {raceEvents.length > 1 && (
            <div className="flex items-center gap-1 mb-3 p-1 bg-slate-100 rounded-lg">
              {raceEvents.map((re) => {
                const isSelected = selectedRaceEventId === re.id;
                return (
                  <button
                    key={re.id}
                    onClick={() => setSelectedRaceEventId(re.id)}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                      ${isSelected
                        ? 'bg-white text-slate-800 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                      }
                    `}
                  >
                    {re.series_logo_URL && (
                      <img src={re.series_logo_URL} alt="" className="h-4 w-auto" />
                    )}
                    {re.series_name || re.name || 'Race Event'}
                  </button>
                );
              })}
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-xs text-red-700 flex-1">{error}</span>
              <button
                onClick={() => setError(null)}
                className="p-0.5 text-red-400 hover:text-red-600 rounded"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Compact Actions Bar */}
          <div className="flex items-center gap-1.5 mb-2 p-2 bg-white border border-slate-200 rounded">
            <Input
              placeholder="Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={Search}
              size="sm"
              className="max-w-[160px]"
            />
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              size="sm"
              className="max-w-[150px]"
              aria-label="Sort entries"
            >
              <option value="last_name">Sort: Last Name</option>
              <option value="car_number">Sort: Car Number</option>
              <option value="pill">Sort: Pill</option>
              <option value="entry_time">Sort: Entry Time</option>
            </Select>
            <div className="flex-1" />
            <Button variant="secondary" size="xs" leftIcon={Copy} onClick={() => setCopyModalOpen(true)} className="leading-tight">
              Copy From...
            </Button>
            <Button variant="secondary" size="xs" leftIcon={Users} onClick={() => setAssignModalOpen(true)} disabled={entries.length === 0} className="leading-tight">
              Assign to Race
            </Button>
            <Button variant="primary" size="xs" leftIcon={Shuffle} onClick={handlePillDraw} className="leading-tight">
              Pill Draw
            </Button>
          </div>

          {/* Entries Drop Zone */}
          <div
            onDrop={handleDropOnEntriesArea}
            onDragOver={handleDragOverEntriesArea}
            onDragLeave={handleDragLeaveEntriesArea}
            className={`min-h-[400px] rounded-lg transition-all ${
              isDraggingOver
                ? 'bg-wrg-blue-600/10 border-2 border-dashed border-wrg-blue-600'
                : 'border-2 border-transparent'
            }`}
          >
            {error && entries.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-slate-400">{error}</p>
                  <Button onClick={fetchData} className="mt-4">Retry</Button>
                </CardContent>
              </Card>
            ) : filteredEntries.length === 0 ? (
              <Card className={`border-dashed ${isDraggingOver ? 'border-wrg-blue-600 bg-wrg-blue-600-50' : 'border-slate-300'}`}>
                <CardContent className="p-12 text-center">
                  <div className={`transition-transform ${isDraggingOver ? 'scale-110' : ''}`}>
                    <UserPlus className={`w-16 h-16 mx-auto mb-4 ${isDraggingOver ? 'text-wrg-blue-600' : 'text-slate-400'}`} />
                    <h3 className="text-lg font-medium text-slate-800 mb-2">
                      {isDraggingOver ? 'Drop to Add Entry' : 'No Entries Yet'}
                    </h3>
                    <p className="text-slate-500 mb-4">
                      {isDraggingOver
                        ? 'Release to add this driver to the entry list'
                        : 'Drag drivers from the sidebar to add them to this event'
                      }
                    </p>
                    {!isDraggingOver && (
                      <Button variant="secondary" onClick={() => setCopyModalOpen(true)} leftIcon={Copy}>
                        Copy from Another Event
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div>
                {/* Column Headers */}
                <div className="flex items-center gap-3 px-3 py-2 mb-2 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex-shrink-0 w-4" /> {/* Drag handle spacer */}
                  <div className="flex-shrink-0 w-7 text-center">Pos</div>
                  <div className="flex-shrink-0 w-14">Car #</div>
                  <div className="flex-1 min-w-0">Driver</div>
                  <div className="flex-shrink-0 w-16 text-center">Pill</div>
                  <div className="flex-shrink-0 w-[68px]" /> {/* Actions spacer */}
                </div>

                {filteredEntries.map((entry, index) => (
                  <DraggableEntryCard
                    key={entry.id}
                    entry={entry}
                    index={index}
                    onEdit={handleEditEntry}
                    onDelete={handleDeleteEntry}
                    onPillChange={handlePillChange}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    isDragging={dragIndex === index}
                    dragOverIndex={dragOverIndex}
                  />
                ))}

                {/* Drop zone at bottom when there are entries */}
                {isDraggingOver && (
                  <div className="border-2 border-dashed border-wrg-blue-600 rounded-lg p-6 text-center bg-wrg-blue-600-50">
                    <UserPlus className="w-8 h-8 mx-auto mb-2 text-wrg-blue-600" />
                    <p className="text-sm text-wrg-blue-600-700">Drop here to add entry</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stats Footer */}
          {entries.length > 0 && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <div className="flex items-center gap-4">
                <span>{entries.length} total entries</span>
                <span>{entries.filter(e => e.pill).length} with pills</span>
              </div>
              <div className="text-xs">
                Drag to reorder • Click edit to modify
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entry Modal - for both adding and editing */}
      <DriverConfirmationModal
        key={entryModalOpen ? (editingEntry?.id ?? pendingDriver?.driver_ID ?? 'new') : 'closed'}
        isOpen={entryModalOpen}
        onClose={handleCloseEntryModal}
        driver={pendingDriver}
        entry={editingEntry}
        mode={entryModalMode}
        onConfirm={handleModalConfirm}
        loading={savingEntry}
      />

      {/* Copy Entries Modal */}
      <CopyEntriesModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        eventId={eventId}
        onImportComplete={handleImportComplete}
      />

      {/* Assign to Race Modal */}
      <AssignToRaceModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        eventId={eventId}
        entries={entries}
        onAssignComplete={() => setAssignModalOpen(false)}
      />
    </div>
  );
}
